import type { ICorridor } from "../shared/session/interfaces/session";
import { countryDisplayName } from "./country-display";

export interface CorridorPair {
  originCountryCode: string;
  destinationCountryCode: string;
}

export const supportedOrigins = (corridors: ICorridor[]): string[] =>
  Array.from(new Set(corridors.map((c) => c.originCountryCode))).sort();

export const supportedDestinations = (
  corridors: ICorridor[],
  originCountryCode: string
): string[] =>
  Array.from(
    new Set(
      corridors
        .filter((c) => c.originCountryCode === originCountryCode)
        .map((c) => c.destinationCountryCode)
    )
  ).sort();

/**
 * Resolves the initial corridor from session config: pinned default, or the
 * first row in `config.corridors` (widget catalog order).
 */
export const resolveInitialCorridor = (
  corridors: ICorridor[],
  defaultCorridor?: CorridorPair
): CorridorPair | undefined => {
  if (defaultCorridor) return defaultCorridor;
  const first = corridors[0];
  if (!first) return undefined;
  return {
    originCountryCode: first.originCountryCode,
    destinationCountryCode: first.destinationCountryCode,
  };
};

export const seedOriginCountry = (
  corridors: ICorridor[],
  defaultCorridor?: CorridorPair
): string | undefined =>
  resolveInitialCorridor(corridors, defaultCorridor)?.originCountryCode;

export const seedDestinationCountry = (
  corridors: ICorridor[],
  defaultCorridor?: CorridorPair
): string | undefined =>
  resolveInitialCorridor(corridors, defaultCorridor)?.destinationCountryCode;

export const findCorridor = (
  corridors: ICorridor[],
  originCountryCode: string,
  destinationCountryCode: string
): ICorridor | null =>
  corridors.find(
    (c) =>
      c.originCountryCode === originCountryCode &&
      c.destinationCountryCode === destinationCountryCode
  ) ?? null;

export interface CorridorCountryOption {
  countryCode: string;
  countryName: string;
  currencyCode?: string;
  effectiveStatus: "AVAILABLE";
}

const FALLBACK_CURRENCY: Record<string, string> = {
  US: "USD",
  MX: "MXN",
  CO: "COP",
  GT: "GTQ",
  BR: "BRL",
  AR: "ARS",
  CL: "CLP",
  PE: "PEN",
  EC: "USD",
  DO: "DOP",
  HN: "HNL",
  SV: "USD",
};

export const currencyForCountryCode = (countryCode: string): string =>
  FALLBACK_CURRENCY[countryCode.toUpperCase()] ?? "USD";

export const countryOptionsFromCodes = (
  codes: string[],
  locale = "en"
): CorridorCountryOption[] =>
  codes.map((countryCode) => {
    const upper = countryCode.toUpperCase();
    return {
      countryCode: upper,
      countryName: countryDisplayName(upper, locale),
      currencyCode: currencyForCountryCode(upper),
      effectiveStatus: "AVAILABLE" as const,
    };
  });
